import React, { useMemo, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import { getDateWiseSectionPaidSummary } from "../../api/feeApi";
import "../Attendance/Attendance.css";

const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => today().slice(0, 8) + "01";

/**
 * Direct port of digicampusbd's `fee-report-date-wise-section-summary`
 * ("Paid Summary Section Wise") — identical aggregation to
 * `fee-report-section-summary-paid-list`'s "Class-Shift-Section Wise"
 * variant, with an added payment-date grouping layer: one sub-table per real
 * payment date, each with its own section × fee-head pivot and subtotal, plus
 * a grand total across all dates. New `GET
 * /api/fee-reports/date-wise-section-paid-summary` mirrors
 * `FeeReportService.dateWiseSectionPaidSummary`'s native query verbatim
 * (the old app also caps the range at 31 days — enforced client-side here).
 */
export default function FeeDateWiseSectionSummaryPage() {
  const [fromDate, setFromDate] = useState(firstOfMonth());
  const [toDate, setToDate] = useState(today());
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSearch = async () => {
    setError("");
    const days = (new Date(toDate) - new Date(fromDate)) / (1000 * 60 * 60 * 24);
    if (days > 31) {
      setError("More than 31 Days is not accepted.");
      return;
    }
    setLoading(true);
    try {
      setRows(await getDateWiseSectionPaidSummary(fromDate, toDate));
    } catch (err) {
      setError("Failed to load section-wise paid summary.");
    } finally {
      setLoading(false);
    }
  };

  const { groupedByDate, feeHeadNames } = useMemo(() => {
    if (!rows) return { groupedByDate: [], feeHeadNames: [] };
    const headSet = new Set();
    rows.forEach((r) => Object.keys(r.details || {}).forEach((h) => headSet.add(h)));
    const byDate = new Map();
    rows.forEach((r) => {
      if (!byDate.has(r.paymentDate)) byDate.set(r.paymentDate, []);
      byDate.get(r.paymentDate).push(r);
    });
    const dates = Array.from(byDate.keys()).sort();
    return { groupedByDate: dates.map((d) => ({ date: d, sections: byDate.get(d) })), feeHeadNames: Array.from(headSet).sort() };
  }, [rows]);

  const rowTotal = (r) => feeHeadNames.reduce((s, h) => s + (r.details[h] || 0), 0);
  const grandTotal = (rows || []).reduce((s, r) => s + rowTotal(r), 0);

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Paid Summary Section Wise</h1>
      </div>
      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}
      <div className="attendance-filters">
        <label>
          From Date
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </label>
        <label>
          To Date
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </label>
        <button type="button" className="btn-primary" onClick={handleSearch}>
          Search
        </button>
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : (
        rows && (
          <div>
            {groupedByDate.map((group) => (
              <div key={group.date} className="table-scroll" style={{ marginBottom: 20 }}>
                <h3>{group.date}</h3>
                <table className="attendance-table">
                  <thead>
                    <tr>
                      <th>Section</th>
                      {feeHeadNames.map((h) => (
                        <th key={h}>{h}</th>
                      ))}
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.sections.map((r) => (
                      <tr key={r.classConfigId}>
                        <td>{r.sectionName}</td>
                        {feeHeadNames.map((h) => (
                          <td key={h}>{(r.details[h] || 0).toFixed(2)}</td>
                        ))}
                        <td>{rowTotal(r).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td style={{ fontWeight: 800 }} colSpan={feeHeadNames.length + 1}>
                        Date Subtotal
                      </td>
                      <td>
                        <strong>{group.sections.reduce((s, r) => s + rowTotal(r), 0).toFixed(2)}</strong>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ))}
            {groupedByDate.length === 0 && <p>No paid invoices found for this selection.</p>}
            <p style={{ fontWeight: 800 }}>Grand Total: {grandTotal.toFixed(2)}</p>
          </div>
        )
      )}
    </MainLayout>
  );
}
